import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { RotateCcw, Package, Clock, CheckCircle, XCircle, MapPin, ArrowRight, Bolt, Mail, Phone } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Axon Muscle';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.returnsBanner}
            alt="Returns policy"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/90 to-neutral-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          {/* Cyan Glow */}
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-axon-600/15 rounded-full blur-[120px]" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-6">
                  <Bolt className="w-4 h-4" />
                  30-DAY GUARANTEE
                </div>
                <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                  Returns & Refunds
                </h1>
                <p className="text-neutral-300 text-lg">
                  We want you to be completely satisfied with your purchase.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Return Address - Prominent Card */}
          <div className="mb-16 p-8 bg-gradient-to-br from-axon-600/20 to-electric-500/10 rounded-3xl border border-axon-500/30">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="w-20 h-20 bg-axon-600/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-10 h-10 text-axon-400" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-2xl text-white uppercase tracking-tight mb-2">Return Address</h2>
                <p className="text-axon-400 font-semibold text-xl">
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Overview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Clock, title: '30-Day Window', desc: 'Return within 30 days of purchase for a full refund' },
              { icon: Package, title: 'Original Packaging', desc: 'Products must be unopened and in original condition' },
              { icon: RotateCcw, title: 'Full Refund', desc: 'Receive a complete refund to your original payment method' },
            ].map((item, i) => (
              <div key={i} className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6">
                <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-axon-500" />
                </div>
                <h3 className="font-display font-bold text-white uppercase tracking-wide mb-2">{item.title}</h3>
                <p className="text-neutral-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {/* Eligible Items */}
            <div className="bg-neutral-900 rounded-2xl border border-neutral-800 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <h2 className="font-display text-xl text-white uppercase tracking-tight">Eligible for Return</h2>
              </div>
              <ul className="space-y-4">
                {[
                  'Unopened products in original packaging',
                  'Products with intact seals',
                  'Items purchased within the last 30 days',
                  'Products with proof of purchase',
                  'Items without visible damage',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Not Eligible Items */}
            <div className="bg-neutral-900 rounded-2xl border border-neutral-800 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <h2 className="font-display text-xl text-white uppercase tracking-tight">Not Eligible</h2>
              </div>
              <ul className="space-y-4">
                {[
                  'Opened or used products',
                  'Products with broken seals',
                  'Items purchased over 30 days ago',
                  'Products without proof of purchase',
                  'Items with visible damage from misuse',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* How to Return */}
          <div className="mt-16">
            <h2 className="font-display text-3xl text-white uppercase tracking-tight text-center mb-10">How to Return</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { num: '01', title: 'Contact Us', desc: 'Email us at ' + CONTACT_INFO.email + ' with your order number' },
                { num: '02', title: 'Get Approval', desc: 'Wait for return authorization from our team' },
                { num: '03', title: 'Ship It', desc: 'Send the product to our return address' },
                { num: '04', title: 'Get Refund', desc: 'Receive your refund within 5-7 business days' },
              ].map((step, i) => (
                <div key={i} className="relative">
                  <div className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-axon-600 to-axon-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-display font-bold text-xl">{step.num}</span>
                    </div>
                    <h3 className="font-display font-bold text-white uppercase tracking-wide mb-2">{step.title}</h3>
                    <p className="text-neutral-400 text-sm">{step.desc}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-neutral-700" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact for Returns */}
          <div className="mt-16 bg-neutral-900 rounded-2xl border border-neutral-800 p-8">
            <h2 className="font-display text-2xl text-white uppercase tracking-tight mb-6 text-center">Need Help With a Return?</h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-3 px-6 py-4 bg-neutral-800 rounded-xl hover:bg-neutral-700 transition-colors"
              >
                <Mail className="w-5 h-5 text-axon-500" />
                <span className="text-white font-medium">{CONTACT_INFO.email}</span>
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-3 px-6 py-4 bg-neutral-800 rounded-xl hover:bg-neutral-700 transition-colors"
              >
                <Phone className="w-5 h-5 text-axon-500" />
                <span className="text-white font-medium">{CONTACT_INFO.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReturnsPage;
